import React, { createContext, useContext, useState, useEffect } from 'react';
import { userService } from '../services/userService'; // Make sure this path is correct
import api from '../services/api'; 
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// A simple loading component for the auth check
const AuthLoadingSpinner = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="text-white text-xl">Initializing Session...</div>
  </div>
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true); // Start as true

  useEffect(() => {
    const checkLoggedIn = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          // You might need to add a token verification endpoint to your userService
          // For now, we assume loadUser does this.
          await loadUser();
        } catch (error) {
          // If token is invalid, logout
          // logout();
          setLoading(false); // Make sure to set loading to false even on error
        }
      } else {
        setLoading(false); // No token, so we are done loading
      }
    };

    checkLoggedIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs only once on mount

  const loadUser = async () => {
    try {
      const userData = await userService.getCurrentUser();
      setUser(userData.user);
      setCredits(userData.user.credits); 
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Failed to load user", error);
      // logout(); // Clear invalid session
      throw error; // Re-throw error so the caller knows it failed
    } finally {
      // This block will run whether the try succeeds or fails
      setLoading(false);
    }
  };


  const login = async (email, password) => {
    try {
      const response = await userService.login(email, password);
      
      // Store tokens
      localStorage.setItem('access_token', response.access);
      localStorage.setItem('refresh_token', response.refresh);
      
      // Store user data in state - this makes it available throughout the app
      setUser(response.user);
      setCredits(response.user.credits);
      setIsAuthenticated(true);
      
      return response;
    } catch (error) {
      // Re-throw the error so Login component can handle it
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await userService.register(userData);
      
      localStorage.setItem('access_token', response.access);
      localStorage.setItem('refresh_token', response.refresh);
      
      setUser(response.user);
      setCredits(response.user.credits);
      setIsAuthenticated(true);
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
            // Call backend to blacklist the refresh token
            await api.post('/users/logout/', { refresh: refreshToken });
        }
    } catch (error) {
        console.error('Logout error:', error);
        // Continue with logout even if backend call fails
    } finally {
        // Clear all auth data regardless of backend response
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        
        setIsAuthenticated(false);
        setCredits(0);
        setUser(null);
    }
  };

  const updateCredits = async () => {
    try {
      const creditsData = await userService.getCredits();
      setCredits(creditsData.available_credits || creditsData.credits);
      
      // Also update the credits in the user object
      if (user) {
        setUser({ ...user, credits: creditsData.available_credits || creditsData.credits });
      }
    } catch (error) {
      console.error('Error updating credits:', error);
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUserData }));
  };


  if (loading) {
    return <AuthLoadingSpinner />;
  }


  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      credits,
      setCredits,
      loading,
      login,
      register,
      logout,
      loadUser,
      updateUser,
      updateCredits
    }}>
      {children}
    </AuthContext.Provider>
  );
};




